/*
 * Linked Options seamless variant switcher (prototype).
 *
 * Hijacks clicks on Linked Options swatches (the King / LinkedOption Combined
 * Listings app, class `lv-option-item-link`) and replaces the product section
 * via Shopify's Section Rendering API instead of a full page reload.
 *
 * Loaded conditionally from layout/theme.liquid only on product pages when
 * `settings.feature_ajax_linked_options_swap` is enabled. Defaults to off.
 *
 * See HANDOVER-LINKED-OPTIONS-SEAMLESS.md for the full functional spec.
 *
 * ─────────────────────────────────────────────────────────────────────
 * EXTERNAL DEPENDENCIES — verify these still hold after any app update
 * ─────────────────────────────────────────────────────────────────────
 * Last tested: 2026-05-11
 *
 * Shopify Section Rendering API
 *   - GET /products/<handle>?sections=<id> returns JSON map of section HTML
 *   - Section endpoint follows locale-handle redirect; .js endpoint does NOT
 *
 * Kaching Bundles app (OPTIONAL — wholesale customers don't see it)
 *   `hasKachingOnPage` (set at script init from a `<kaching-bundle>` element
 *   presence check) controls whether any of the Kaching-related code paths
 *   execute. Wholesale pages skip the page fetch, the bundle preservation,
 *   the overlay cross-fade, and the buy_buttons min-height pin entirely.
 *   When Kaching IS in use, all of the below applies:
 *   - <kaching-bundle> wrapper element (created by Kaching's loader on init)
 *   - <kaching-bundles-block> inner custom element (CLEARS its children on
 *     construction — so cloneNode on <kaching-bundle> returns an empty shell;
 *     clone <div class="kaching-bundles"> one level deeper instead)
 *   - .kaching-bundles, .kaching-bundles__bar, .kaching-bundles__bar-title
 *   - .kaching-bundles__collection-product__title (item label in first bar —
 *     contains the active product name a SECOND time)
 *   - .kaching-bundles__block--loaded (deals-data-loaded flag)
 *   - .kaching-bundles__checkbox-upsells, .kaching-bundles__checkbox-upsells__upsell
 *     (last bar is the upsell — loads ~150ms async after sync 3-bar render)
 *   - script.kaching-bundles-product, script.kaching-bundles-deal-block-settings
 *     (live OUTSIDE the section; product-specific config data)
 *   - window.kachingBundlesInitialize() — kicks Kaching to rebuild bundles
 *     for the current scripts. Mutates form variant-id input as a side effect;
 *     SKIPS rebuild if <kaching-bundle> already exists.
 *
 * Judge.me Reviews app
 *   - .jdgm-* prefix on the small review widget rendered inside a `[data-block-
 *     type="liquid"]` block-item under the title.
 *
 * LinkedOption Combined Listings (King) app
 *   - a.lv-option-item-link swatches with data-handle attribute (href always
 *     uses the EN slug regardless of locale — see fetchProductBundle comment)
 *   - .lv-option-item__active class on the currently-selected swatch
 *   - <king-linked-options-product> custom element wraps the swatch group;
 *     its connectedCallback compares URL to active product and navigates if
 *     mismatched — replaceState BEFORE innerHTML swap to avoid this.
 *
 * Maestrooo Impact theme
 *   - section[id^="shopify-section-template--"][class*="main-product"]
 *   - safe-sticky.product-info wraps the right-side product details column
 *   - [data-block-id="buy_buttons"] block-item houses the ATC form AND the
 *     JS-injected kaching-bundle (kaching is a CHILD of buy_buttons)
 *   - The buy form (.product-info__buy-buttons) has margin: 15px 0 15px
 *     that propagates out of buy_buttons in the natural cascade; pinning
 *     min-height stops that propagation — we re-apply margin-bottom !important.
 *
 * GRACEFUL DEGRADATION
 *   If any of these break:
 *   - Missing Kaching API → applyKachingUpdate early-returns. Bundle re-renders
 *     on its own (visible flicker) but the section swap itself still works.
 *   - Missing Judge.me selectors → review widget gets re-rendered fresh
 *     (visible flicker for ~200ms but functional).
 *   - Missing Linked Options swatches → click handler never matches, swatches
 *     do their default full-page nav. NO regression vs. baseline.
 *   - applySwap throws → catch block falls back to window.location.href = href.
 *   - applyKachingUpdate throws → swallowed; bundle stays as the patched
 *     preserved (visually correct, internal state for OLD product). User
 *     pages around fix it on next nav.
 *   - Health check at init logs to console.warn so issues are visible during
 *     QA without breaking the flow.
 */
(function () {
  'use strict';

  /* Detect the main product section ID at runtime — template IDs differ
     between themes (a duplicated theme gets fresh IDs), so the value from
     the handover doc (`template--27831547855114__main`) only matched the
     original theme. The canonical "what is the main product section on this
     page" lookup is by class. */
  function detectMainSection() {
    var el = document.querySelector('section[id^="shopify-section-template--"][class*="main-product"]');
    if (el && el.id) {
      return { domId: el.id, sectionId: el.id.replace('shopify-section-', '') };
    }
    /* Fallback: any section that contains the Linked Options product element. */
    var loRoot = document.querySelector('king-linked-options-product');
    if (loRoot) {
      var wrapper = loRoot.closest('section[id^="shopify-section-"]');
      if (wrapper && wrapper.id) {
        return { domId: wrapper.id, sectionId: wrapper.id.replace('shopify-section-', '') };
      }
    }
    return null;
  }

  var detected = detectMainSection();
  if (!detected) {
    /* Not a product page (or layout has changed) — bail without binding. */
    return;
  }
  var SECTION_ID = detected.sectionId;
  var SECTION_DOM_ID = detected.domId;

  /* Two separate concepts, both needed downstream:
       - `kachingOnPage()`: is a Kaching bundle currently rendered on
         this page? Wholesale customers don't get the Kaching block
         (gated on a customer-tag check in the theme), AND many retail
         products simply don't have Kaching deals configured. In both
         cases there's nothing to wait for during a swap — we skip the
         page fetch, the init call, AND the loading-state CSS that
         hides the quantity stepper.
         Re-evaluated at click time (not cached at script init) because
         a) Kaching's loader runs async and may not have rendered the
         bundle yet at script-init time, and b) the linked-options
         group can include products with and without Kaching bundles —
         the check has to reflect THE CURRENT product's actual state.
         Detection: the `<kaching-bundle>` element is server-rendered as
         an EMPTY placeholder on every retail page; Kaching's loader
         fills it with a `<kaching-bundles-block>` ONLY when there are
         deals to show. So "has child content" is the reliable signal.
       - `seamlessKaching()`: theme-settings sub-toggle AND
         kachingOnPage(). When the sub-toggle is OFF but a bundle IS on
         the page, we still fetch + init (otherwise the bundle stays
         gone forever — Kaching's own MutationObserver doesn't fire on
         our innerHTML swap), but skip the preserve/shroud/dim. */
  function kachingOnPage() {
    if (window.__loIsWholesale) return false;
    /* Detect Kaching via the `<script class="kaching-bundles-deal-block-settings">`
       tag's CONTENT, not via the `<kaching-bundle>` element.
       Why not the kaching-bundle element: it gets wiped by every
       section.innerHTML swap and is only re-injected by Kaching's
       init. After a fast switch where A's init rendered a bundle
       that B's innerHTML then wiped, the element is missing — using
       its presence would return false on every subsequent swap, all
       Kaching paths would skip, and the bundle would stay missing
       until a full reload.
       Why not just the product script: present even on products
       that have no Kaching deal configured (e.g. dungeons-kittens-mok
       has the product script but no bundle to show). Treating those
       as "Kaching on page" would hide the quantity stepper for no
       reason.
       The deal-block-settings script is the right signal: it lives
       in document.body (survives innerHTML swaps), is swapped to the
       new product's content by applyKachingUpdate, and is empty when
       no deal is configured for the current product. Empty content
       = no Kaching bundle, non-empty = there's a deal to render. */
    var dealScript = document.querySelector('script.kaching-bundles-deal-block-settings');
    return !!dealScript && dealScript.textContent.trim().length > 0;
  }
  function seamlessKaching() {
    return kachingOnPage() && !!window.__loSeamlessKaching;
  }
  /* Whether to preserve the Judge.me review widget across swaps. Same
     pattern: theme-settings sub-toggle. When off, the widget gets
     re-rendered fresh on each swap (brief flicker, still functional). */
  var preserveJudgeMe = !!window.__loSeamlessJudgeMe;

  /* CSS that disables the Add-to-Cart form (button, quantity, native
     variant selectors) while a swatch swap is in flight.
     Bug it fixes: between swatch-click and the section-innerHTML
     replacement (~700ms), the form on the page is still the OLD
     product's form with the OLD variant id. If the customer clicks
     "Add to cart" in that window, the OLD product gets added.
     During Kaching's re-init (in seamless mode) there's also a
     sub-millisecond window where Kaching mutates the variant id back
     to the OLD product's variant before our restore. Better safe.
     Gate via `[data-lo-swap-in-progress]` on the section element —
     that attribute survives `section.innerHTML = newInner` because we
     only replace the section's CHILDREN, not the section itself.
     Targets the form (not buy_buttons whole) so the Kaching bundle
     above the form doesn't gray out alongside it. */
  (function injectLoaderStyles() {
    /* The Impact theme's ATC button renders as:
         <button type="submit" is="custom-button" class="button button--xl">
           <div>Add to cart</div>
           <span class="button__loader">...</span>
         </button>
       We put the spinner INSIDE the inner <div>, before its text node,
       via `> div::before` — inline-block + margin-right for the gap
       gives the merchant-requested "loader just before the text with
       some nice padding". The button's outer block layout is untouched. */
    var css = ''
      + 'section[data-lo-swap-in-progress] [data-block-id="buy_buttons"] form[action*="/cart/add"] {'
      + '  pointer-events: none;'
      + '  opacity: 0.6;'
      + '  cursor: wait;'
      + '  transition: opacity 120ms ease;'
      + '}'
      + 'section[data-lo-swap-in-progress] [data-block-id="buy_buttons"] form[action*="/cart/add"] button[type="submit"] > div::before {'
      + '  content: "";'
      + '  display: inline-block;'
      + '  vertical-align: -3px;'
      + '  width: 16px; height: 16px;'
      + '  margin-right: 10px;'
      + '  border: 2px solid currentColor;'
      + '  border-top-color: transparent;'
      + '  border-radius: 50%;'
      + '  animation: lo-swap-spin 0.7s linear infinite;'
      + '  opacity: 0.85;'
      + '}'
      + '@keyframes lo-swap-spin { to { transform: rotate(360deg); } }'
      /* Bundle "loading" treatment during a swatch swap.
         Visual: dim every direct child of `<kaching-bundle>` to 0.4
         opacity and disable interactions, then show a spinner via
         the `::after` pseudo on the kaching-bundle host itself.
         Why dim the CHILDREN and not the kaching-bundle host? Opacity
         stacks down the tree, so a spinner pseudo-element inside a
         dimmed host would be ~0.28 effective (0.4 * 0.7) — barely
         visible. By dimming only `> *` we keep the host at full
         opacity, so the pseudo-spinner reads at its declared opacity.
         The lock must stay engaged from the click moment until
         kachingBundlesInitialize has re-rendered the new product's
         bundle AND the user's pre-swap selection has been re-applied.
         Otherwise any mid-swap click on the bundle (or on the
         intermediate state where Kaching has pushed the OLD product's
         data into the new placeholder) gets wiped by a downstream
         render. */
      + 'section[data-lo-bundle-loading] [data-block-id="buy_buttons"] kaching-bundle {'
      + '  position: relative;'
      + '  display: block;'
      + '}'
      /* While loading AND we have a shroud in place: hide the new
         kaching-bundle that Kaching's MutationObserver injects (it's
         NOT in the section's server-rendered HTML). The shroud is a
         plain <div> snapshot of the OLD bundle's rendered markup,
         so it doesn't satisfy Kaching's "is there already a bundle?"
         check — Kaching still injects a fresh one. The shroud stays
         visible (dimmed) so the user keeps seeing the bundle area.
         When applyKachingUpdate finishes, the shroud is removed and
         the new bundle becomes visible naturally.
         The `[data-lo-has-preserved]` gate prevents accidentally
         hiding the new bundle when there was no OLD bundle to
         snapshot (e.g. first swap before Kaching had finished its
         initial render). */
      + 'section[data-lo-bundle-loading][data-lo-has-preserved] [data-block-id="buy_buttons"] kaching-bundle {'
      + '  display: none;'
      + '}'
      /* Hide the quantity_selector block entirely during loading. It
         lives in its own block (`data-block-id="quantity_selector"`),
         separate from the bundle. Showing it while the bundle is
         settling lets the customer change the quantity for a state
         that's still loading — confusing. `display: none` over
         opacity-dim because the merchant wants it hidden, not just
         de-emphasised. When the loading flag clears (end of
         finishLoading), the rule stops applying and the stepper
         reappears in its natural slot. */
      + 'section[data-lo-bundle-loading] [data-block-id="quantity_selector"] {'
      + '  display: none;'
      + '}'
      + 'section[data-lo-bundle-loading] [data-block-id="buy_buttons"] [data-lo-bundle-shroud] {'
      + '  position: relative;'
      + '  display: block;'
      + '}'
      + 'section[data-lo-bundle-loading] [data-block-id="buy_buttons"] [data-lo-bundle-shroud] > * {'
      + '  opacity: 0.4;'
      + '  pointer-events: none;'
      + '  cursor: wait;'
      + '  transition: opacity 200ms ease;'
      + '}'
      /* Spinner centered via margin offsets — NOT via `transform:
         translate(-50%, -50%)`, because the spin animation sets
         `transform: rotate(360deg)` which would overwrite that
         translate every frame and the spinner would "drift off"
         to the top-left as it rotates around its corner. Margin
         centering doesn't conflict with the transform animation. */
      + 'section[data-lo-bundle-loading] [data-block-id="buy_buttons"] [data-lo-bundle-shroud]::after {'
      + '  content: "";'
      + '  position: absolute;'
      + '  top: 50%; left: 50%;'
      + '  margin: -14px 0 0 -14px;'
      + '  width: 28px; height: 28px;'
      + '  border: 3px solid currentColor;'
      + '  border-top-color: transparent;'
      + '  border-radius: 50%;'
      + '  animation: lo-swap-spin 0.7s linear infinite;'
      + '  opacity: 0.7;'
      + '  pointer-events: none;'
      + '}';
    var style = document.createElement('style');
    style.dataset.loSeamlessStyle = '1';
    style.textContent = css;
    document.head.appendChild(style);
  })();

  /* Health check: log warnings for any missing dependencies we rely on.
     Surfaces breakage to the browser console immediately on page load
     rather than waiting for a swap to fail mid-flight. Doesn't abort —
     each runtime path has its own defensive checks. Defer to after
     DOMContentLoaded so app-embed JS has had a chance to inject its
     elements (Kaching, Judge.me). */
  function runHealthCheck() {
    var section = document.getElementById(SECTION_DOM_ID);
    if (!section) return;
    var checks = [
      { name: 'Linked Options swatches', ok: !!document.querySelector('a.lv-option-item-link[data-handle]') },
      { name: 'Linked Options custom element', ok: !!document.querySelector('king-linked-options-product') },
      { name: 'Buy buttons block-item', ok: !!section.querySelector('[data-block-id="buy_buttons"]') }
    ];
    /* Judge.me dep is only required when its sub-toggle is on. */
    if (preserveJudgeMe) {
      checks.push({ name: 'Judge.me review block', ok: !!section.querySelector('[class*="jdgm-"]') });
    }
    /* Kaching config script + init API are required whenever Kaching is
       rendered on this page — both seamless and minimum-effort paths
       depend on them. Skip the warning for wholesale customers (no
       Kaching block on their pages). */
    if (kachingOnPage()) {
      checks.push(
        { name: 'Kaching product config script', ok: !!document.querySelector('script.kaching-bundles-product') },
        { name: 'Kaching init API', ok: typeof window.kachingBundlesInitialize === 'function' }
      );
    }
    /* Bar-title selectors are only used by the seamless clone/patch
       path. Skip checking them when the sub-toggle is off. */
    if (seamlessKaching()) {
      checks.push(
        { name: 'Kaching bundle-title selector', ok: !!section.querySelector('.kaching-bundles__bar-title') },
        { name: 'Kaching collection-product-title selector', ok: !!section.querySelector('.kaching-bundles__collection-product__title') }
      );
    }
    var failed = checks.filter(function (c) { return !c.ok; });
    if (failed.length) {
      console.warn('[LO seamless] dependency check found ' + failed.length + ' missing:',
        failed.map(function (c) { return c.name; }).join(', '),
        '\nThe feature will degrade gracefully but some pieces may flicker. See linked-options-seamless.js header for what each dep covers.');
    }
  }
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    setTimeout(runHealthCheck, 3000); /* let Kaching/Judge.me finish injecting */
  } else {
    window.addEventListener('DOMContentLoaded', function () { setTimeout(runHealthCheck, 3000); });
  }

  /* Locale-aware root, e.g. "/" or "/nl-nl/". Falls back to "/" if not set. */
  function rootPrefix() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* Capture the title template at script init so we can preserve any
     " – Shop Name" / " | Shop Name" suffix when we swap titles. */
  var initialTitle = document.title || '';
  var titleSuffix = (function () {
    var seps = [' – ', ' | ', ' — ', ' - '];
    for (var i = 0; i < seps.length; i++) {
      var idx = initialTitle.indexOf(seps[i]);
      if (idx > 0) return initialTitle.substring(idx);
    }
    return '';
  })();

  /* Latest click wins. Each new click cancels any in-flight fetch and
     advances a counter; resolved fetches whose counter no longer matches
     are dropped to avoid an older, slower response overwriting a newer
     swap. Without this, clicking A → B → C rapidly can settle on B if
     C's fetch resolves before B's does. */
  var activeController = null;
  var latestClickId = 0;

  /* User's Kaching selection state captured just before a swap fires,
     to be restored once Kaching has re-rendered for the new product.
     Shape: { selectedDealBarId, selectedDealBarIndex, checkedUpsellIndices }.
     Stored at module scope (not inside applySwap) because the capture
     happens BEFORE the fetch resolves, and the restore happens LATER
     inside applyKachingUpdate after the page fetch + kaching init.
     Cleared once consumed, OR overwritten if the user clicks a new
     swatch mid-flight. Null means "nothing to restore". */
  var pendingKachingState = null;

  /* Read which deal bar (radio-style "buy 1/2/3" picker) is selected
     and which upsells (Premium Dice Tray etc.) are checked off the
     given section's `<kaching-bundle>`. Returns null if the bundle
     isn't there. Identifies deal bars by their `data-deal-bar-id`
     (stable across linked-options products since the deals are
     shared Kaching configs) and falls back to position-in-list if
     the id is missing. Upsells are identified by index (they have
     no stable id we can read). */
  function captureKachingState(section) {
    if (!section) return null;
    var kb = section.querySelector('kaching-bundle');
    if (!kb) return null;
    var bars = kb.querySelectorAll('.kaching-bundles__bar');
    if (!bars.length) return null;
    var selectedDealBarId = null;
    var selectedDealBarIndex = -1;
    var checkedUpsellIndices = [];
    for (var i = 0; i < bars.length; i++) {
      var bar = bars[i];
      var isUpsell = bar.classList.contains('kaching-bundles__checkbox-upsells__upsell');
      if (isUpsell) {
        var cb = bar.querySelector('[role="checkbox"]');
        if (cb && cb.getAttribute('aria-checked') === 'true') {
          checkedUpsellIndices.push(i);
        }
      } else {
        if (bar.classList.contains('kaching-bundles__bar--selected')) {
          selectedDealBarIndex = i;
          selectedDealBarId = bar.dataset.dealBarId || null;
        }
      }
    }
    /* Default (bar 0 selected + no upsells) is what Kaching always
       renders fresh, so we can skip work if that's what we captured. */
    var isDefault = selectedDealBarIndex <= 0 && checkedUpsellIndices.length === 0;
    if (isDefault) return null;
    return {
      selectedDealBarId: selectedDealBarId,
      selectedDealBarIndex: selectedDealBarIndex,
      checkedUpsellIndices: checkedUpsellIndices,
    };
  }

  /* One-shot apply of a captured Kaching state to the (now re-rendered)
     bundle. Programmatic interactions:
       - Deal bar: click the radio input (the bar's outer element
         doesn't trigger Kaching's selection handler — only the radio).
       - Upsell: click the `[role="checkbox"]` div (which toggles
         aria-checked + fires Kaching's checkbox handler).
     Returns true if applied (bars were present), false if not yet ready
     so the caller can keep polling. Bails silently if the new bundle's
     bar count doesn't match what we captured (e.g. merchant changed
     Kaching config between products). */
  function applyKachingStateSync(section, state) {
    if (!state || !section) return false;
    var kb = section.querySelector('kaching-bundle');
    var bars = kb ? kb.querySelectorAll('.kaching-bundles__bar') : [];
    var maxIdx = Math.max(
      state.selectedDealBarIndex,
      state.checkedUpsellIndices.length ? Math.max.apply(null, state.checkedUpsellIndices) : -1
    );
    if (bars.length <= maxIdx) return false;
    /* Restore deal-bar selection. Prefer matching by data-deal-bar-id
       (robust to bar reorders); fall back to index. */
    if (state.selectedDealBarIndex > 0) {
      var dealBar = null;
      if (state.selectedDealBarId) {
        dealBar = kb.querySelector(
          '.kaching-bundles__bar[data-deal-bar-id="' + state.selectedDealBarId + '"]'
        );
      }
      if (!dealBar) dealBar = bars[state.selectedDealBarIndex] || null;
      if (dealBar && !dealBar.classList.contains('kaching-bundles__bar--selected')) {
        var radio = dealBar.querySelector('input[type="radio"]');
        if (radio) radio.click();
      }
    }
    /* Restore upsell checkbox states. */
    for (var i = 0; i < state.checkedUpsellIndices.length; i++) {
      var idx = state.checkedUpsellIndices[i];
      var upsellBar = bars[idx];
      if (!upsellBar) continue;
      var cb = upsellBar.querySelector('[role="checkbox"]');
      if (cb && cb.getAttribute('aria-checked') !== 'true') cb.click();
    }
    return true;
  }

  /* Cached vertical footprint AND bar count of the Kaching bundle,
     captured once from the OLD section right before the first swap
     and reused forever after. The user's spec guarantees the bundle
     is structurally identical across linked-options products in this
     theme (same height, same number of bars), so a single measurement
     suffices.
     Height is pinned via `min-height` on the new section's empty
     `<kaching-bundle>` placeholder right after the innerHTML swap so
     the page below doesn't jump while Kaching re-renders.
     Bar count is the target the post-swap polling loop waits for
     before un-dimming the bundle — Kaching renders deal bars
     synchronously but the upsell bar lands async ~150-300ms later,
     so waiting for the full count avoids a "3 bars un-dim → 4th
     pops in" cosmetic glitch. */
  var reservedKachingBlockHeight = null;
  var reservedKachingBarCount = 0;
  function captureBundleMetrics(section) {
    if (reservedKachingBlockHeight && reservedKachingBarCount) return;
    if (!section) return;
    var kb = section.querySelector('kaching-bundle');
    if (kb && kb.offsetHeight > 0) {
      reservedKachingBlockHeight = kb.offsetHeight;
    }
    if (kb) {
      var bars = kb.querySelectorAll('.kaching-bundles__bar');
      if (bars.length) reservedKachingBarCount = bars.length;
    }
  }

  /* Two fetches in parallel:
       - Section fetch (~110KB): drives the fast UI swap.
       - Full-page fetch (~200KB gzipped): used only to extract Kaching's
         product config scripts. They live in an app embed OUTSIDE the
         section (theme app extension at the document level), so they're
         NOT in the section response. Without refreshed scripts, calling
         `kachingBundlesInitialize()` would rebuild bundles for the OLD
         product (and reset the form's variant id to the wrong value).
     We apply the section swap as soon as the first fetch resolves so
     the UI feels instant; bundles re-render a moment later when the
     page fetch completes.
     The .js endpoint we used to fetch for title is gone — see comment
     on `parseSectionResponse` below; we extract the H1 from the section. */
  /* In-memory cache of page HTML by handle. The page HTML is only used
     to extract Kaching's product-config script tags — those are stable
     for a given product (price / bundle structure changes invalidate
     them via a theme edit which forces a full reload anyway). Caching
     here avoids hammering Shopify on ping-pong swaps (A → B → A → B …)
     and avoids tripping Cloudflare's bot-detect rate-limit which
     returns 429 and breaks the swap. Per-page-load cache only (cleared
     when the user fully navigates / reloads). */
  var pageHtmlCache = Object.create(null);

  function fetchProductBundle(handle, signal) {
    var root = rootPrefix();
    var sectionUrl = root + 'products/' + encodeURIComponent(handle) + '?sections=' + SECTION_ID;
    /* NOTE: do NOT send `Accept: application/json` — Shopify routes product
       URLs with that header to the product JSON endpoint and ignores
       `?sections=`, returning the product detail object instead of the
       section-rendering map. */
    var sectionPromise = fetch(sectionUrl, { signal: signal }).then(function (r) {
      if (!r.ok) throw new Error('section fetch ' + r.status);
      return r.text().then(function (text) {
        var sections;
        try { sections = JSON.parse(text); }
        catch (e) { throw new Error('section JSON parse failed: ' + e.message); }
        return { sections: sections, finalUrl: r.url };
      });
    });
    /* Page fetch — only needed to extract Kaching's product config
       scripts (they live in an app embed OUTSIDE the section response).
       Wholesale pages don't render the Kaching block at all so we skip
       the fetch entirely, saving ~200KB of transfer per swap. The
       section swap alone is enough on those pages.
       When Kaching IS on the page but the seamless sub-toggle is off
       we still need this fetch — without the script swap + init nudge,
       Kaching's own MutationObserver doesn't pick up the new section
       and the bundle stays gone forever.
       The promise resolves to either the HTML string or `false` on
       failure (NOT null — null vs false matters in applyKachingUpdate's
       early-return logic so it can distinguish "wholesale / not needed"
       from "fetch genuinely failed"). */
    var pagePromise;
    if (kachingOnPage()) {
      if (pageHtmlCache[handle]) {
        pagePromise = Promise.resolve(pageHtmlCache[handle]);
      } else {
        var pageUrl = root + 'products/' + encodeURIComponent(handle);
        pagePromise = fetch(pageUrl, { signal: signal })
          .then(function (r) {
            if (!r.ok) throw new Error('page fetch ' + r.status);
            return r.text();
          })
          .then(function (text) {
            pageHtmlCache[handle] = text;
            return text;
          })
          .catch(function (err) {
            if (err && err.name === 'AbortError') return null;
            return false; /* genuine failure — applyKachingUpdate treats this distinctly */
          });
      }
    } else {
      pagePromise = Promise.resolve(null);
    }
    return { sectionPromise: sectionPromise, pagePromise: pagePromise };
  }

  /* Strip "?sections=…" off a URL so we have a clean canonical URL to push
     to history. The section endpoint redirects to the canonical localized
     slug but keeps the `?sections=` query param on the final URL. */
  function cleanSectionUrl(rawUrl) {
    try {
      var u = new URL(rawUrl, window.location.origin);
      u.searchParams.delete('sections');
      return u.toString();
    } catch (e) {
      return rawUrl.split('?')[0];
    }
  }

  /* Parses the section's rendered HTML once and extracts both the inner
     HTML (to swap in) and the product title (from the first <h1>). The
     `?sections=` response includes the wrapper <div id="shopify-section-...">,
     so we drill into it before reading innerHTML — otherwise we'd nest a
     wrapper inside the existing one on apply. */
  function parseSectionResponse(fullHtml) {
    var parser = new DOMParser();
    var doc = parser.parseFromString(fullHtml, 'text/html');
    var sectionEl = doc.getElementById(SECTION_DOM_ID);
    if (!sectionEl) return { inner: null, title: null };
    var h1 = sectionEl.querySelector('h1');
    return {
      inner: sectionEl.innerHTML,
      title: h1 ? h1.textContent.trim() : null
    };
  }

  function applySwap(bundle, fallbackUrl) {
    var sectionHtml = bundle.sections[SECTION_ID];
    if (!sectionHtml) throw new Error('section ' + SECTION_ID + ' missing from response');
    var parsed = parseSectionResponse(sectionHtml);
    if (parsed.inner == null) throw new Error('could not extract inner HTML for ' + SECTION_DOM_ID);

    var section = document.getElementById(SECTION_DOM_ID);
    if (!section) throw new Error('section element ' + SECTION_DOM_ID + ' not in DOM');

    /* Extract the server-rendered variant id from the new section HTML
       BEFORE we swap it in. We need it later to repair the form input
       value — see the long comment near the post-swap force-set below.
       Regex over the raw HTML string is robust: it doesn't trigger any
       custom-element callbacks (which would happen if we parsed into a
       Document and then read .value off the input). */
    var newVidMatch = parsed.inner.match(/<input[^>]+name="id"[^>]*\bvalue="(\d+)"/);
    var newServerVid = newVidMatch ? newVidMatch[1] : null;

    /* Measure the bundle's height BEFORE replacing innerHTML so we can
       pin it on the new (empty) <kaching-bundle> placeholder. Without
       the pin, the section briefly collapses to the placeholder's
       intrinsic size (~0) until Kaching re-renders, and the page
       below jumps up then back down. Cached after first capture
       (heights are stable across linked-options products). */
    captureBundleMetrics(section);

    /* Snapshot the OLD bundle into a "shroud": a plain <div> containing
       the OLD bundle's rendered HTML, with the `<kaching-bundle>` and
       `<kaching-bundles-block>` custom-element tags renamed to <div>s
       so the snapshot doesn't trigger Kaching's MutationObserver (it
       only watches for real kaching-bundle elements). This is what
       the user sees during the swap window — dimmed via CSS — instead
       of a blank space.
       Why string-rename instead of cloneNode? `<kaching-bundles-block>`
       empties itself on construction, so cloneNode(true) produces a
       hollow shell. innerHTML capture + tag rename gives us a static
       snapshot whose nested custom elements don't re-construct.
       The shroud is removed in applyKachingUpdate's finishLoading
       once the new (fresh) Kaching bundle has rendered. */
    var preservedShroud = null;
    if (seamlessKaching()) {
      /* Reuse the existing shroud if one's already in the section (rapid
         click during an in-flight swap). The existing shroud reflects
         what the user is CURRENTLY seeing — using a fresh snapshot of
         the (currently hidden) new kaching-bundle would either grab
         A's intermediate state or an empty bundle, causing a visible
         jump from "what the user was watching" to "wrong content".
         Detach (not remove) so the shroud survives the innerHTML swap
         below and gets re-inserted into the new section in the
         re-insert block further down. */
      var existingShroud = section.querySelector('[data-lo-bundle-shroud]');
      if (existingShroud) {
        existingShroud.remove();
        preservedShroud = existingShroud;
      } else {
        /* First click of this gesture chain — snapshot the OLD bundle.
           Only snapshot when the OLD bundle actually has visible
           content (kachingOnPage() already checks this for the
           wholesale + empty-bundle case, but `oldKb.children.length`
           is the same check applied directly to the element we're
           about to clone). */
        var oldKb = section.querySelector('kaching-bundle');
        if (oldKb && oldKb.children.length > 0) {
          var oldHtml = oldKb.outerHTML
            .replace(/<kaching-bundle\b/g, '<div data-lo-bundle-shroud="1"')
            .replace(/<\/kaching-bundle>/g, '</div>')
            .replace(/<kaching-bundles-block\b/g, '<div data-lo-was-block')
            .replace(/<\/kaching-bundles-block>/g, '</div>');
          var temp = document.createElement('div');
          temp.innerHTML = oldHtml;
          preservedShroud = temp.firstElementChild;
        }
      }
    }
    /* Defensive cleanup of any remaining stale shrouds (shouldn't fire
       — preservedShroud either grabbed the single existing one above
       or no shroud was present). */
    var staleShrouds = section.querySelectorAll('[data-lo-bundle-shroud]');
    staleShrouds.forEach(function (el) { el.remove(); });

    /* Preserve the Judge.me review-line block (the small star rating
       under the title). Reviews are aggregate across linked products
       — they're identical for every variant — so re-rendering this
       block is wasted work AND it briefly disappears/flickers while
       Judge.me's widget JS re-initializes after the section swap.
       The big reviews block at the bottom of the page is a separate
       Shopify section so it's untouched by us either way.
       Detection: the liquid block that contains a `.jdgm-` element.
       Gated on theme-settings sub-toggle — when off, the widget just
       re-renders fresh on each swap (safe but visible flicker). */
    var preservedReviewBlock = null;
    if (preserveJudgeMe) {
      var liquidBlocks = section.querySelectorAll('[data-block-type="liquid"]');
      liquidBlocks.forEach(function (block) {
        if (!preservedReviewBlock && block.querySelector('[class*="jdgm-"]')) {
          preservedReviewBlock = block;
        }
      });
    }

    /* Preserve the entire <king-linked-options-product> swatch container.
       Linked Options uses a CSS transform on .lv-option-slide-items to
       slide the swatch row so the active swatch stays in view. When the
       section innerHTML is swapped, the new slide-items element starts
       with no transform applied and snaps to its new position (the new
       active is at a different index than the old active, so the
       transform changes) — visible to the user as a "variant bar jumping
       to a different position".
       Preserving the OLD element means the existing transform stays
       continuous. We then update the .lv-option-item__active class on
       the swatch matching the new product handle. The `transition:
       transform 0.5s` rule already on .lv-option-slide-items will
       smoothly animate any subsequent transform change (e.g. if LO's
       JS recomputes the slide for the new active). */
    var preservedSwatchEl = section.querySelector('king-linked-options-product');

    /* Prefer the section fetch's final URL (post-redirect to the canonical
       localized slug). Falls back to the swatch's own href if for some
       reason finalUrl is missing. */
    var pushUrl = bundle.finalUrl ? cleanSectionUrl(bundle.finalUrl) : fallbackUrl;

    /* Update URL FIRST, BEFORE replacing innerHTML. Linked Options' own
       <king-linked-options-product> custom element runs its connectedCallback
       on insertion and compares the current URL to the active product; if the
       URL still points to the *previous* product, the app treats it as a
       mismatch and navigates the page (defeating our swap). Setting the URL
       before the swap means the new element's first-paint check sees the
       correct URL and doesn't intervene.
       `replaceState` (not pushState) so back-button goes to whatever the
       user came from (e.g. the collection page), not the previous variant. */
    if (pushUrl && pushUrl !== window.location.href) {
      history.replaceState({ url: pushUrl }, '', pushUrl);
    }

    /* Swap the content. The Impact theme's custom elements (<product-rerender>,
       <product-gallery>, <product-quick-add>, …) use standard
       connectedCallback, so they auto-upgrade on insertion. */
    section.innerHTML = parsed.inner;

    /* Repair AND defend the form's variant id.
       Bug it fixes: Kaching's `_updateIdInput` (called from `updateItem`
       on its <kaching-bundle> internal state changes) writes the
       PREVIOUS product's variant id into the form's input[name="id"]
       multiple times between the innerHTML swap and when
       applyKachingUpdate finally calls `kachingBundlesInitialize` with
       the NEW product's bundle data. Confirmed by stack-trace spy:
       `at hn._updateIdInput (kaching-bundles.js)` setting OLD vid
       AFTER our initial force-set, repeatedly, until applyKachingUpdate
       finishes (~400-1000ms later).
       Result without this defender: form action points to /cart/add but
       input[name="id"] holds the OLD product's vid for the duration of
       the swap. If the user clicks ATC during that window, they add the
       OLD product.
       Approach: ONE-SHOT force-set (lines below) handles the easy case
       where nothing overwrites. Then install a `defender` MutationObserver
       that re-asserts the correct vid every time the attribute changes
       (Kaching's IDL value-set fires `value` attribute mutations because
       the IDL setter internally syncs the content attribute).
       Defender is disconnected in applyKachingUpdate after Kaching's own
       state is updated to NEW, AND in handleSwatchClick at the start of
       a new click (so the next swap's defender takes over without the
       old one fighting for a stale vid). */
    if (newServerVid) {
      var defendInputs = function () {
        var inputs = section.querySelectorAll(
          'form[action*="/cart/add"] input[name="id"]'
        );
        for (var vi = 0; vi < inputs.length; vi++) {
          if (inputs[vi].value !== newServerVid) {
            inputs[vi].value = newServerVid;
          }
        }
      };
      defendInputs();

      /* Disconnect any prior defender (from a swap that didn't complete
         its applyKachingUpdate before this one started). */
      if (section.__loVidDefender) {
        try { section.__loVidDefender.disconnect(); } catch (e) { /* no-op */ }
      }
      var defender = new MutationObserver(defendInputs);
      defender.observe(section, {
        subtree: true,
        attributes: true,
        attributeFilter: ['value'],
      });
      section.__loVidDefender = defender;
      section.__loVidDefenderTarget = newServerVid;
    }

    /* Restore the preserved Judge.me review block. The new section's
       fresh copy of this block-item gets thrown away in favour of the
       one we already rendered. Match by data-block-id so we hit the
       right slot. */
    if (preservedReviewBlock && preservedReviewBlock.dataset.blockId) {
      var newReviewBlock = section.querySelector(
        '[data-block-id="' + preservedReviewBlock.dataset.blockId + '"]'
      );
      if (newReviewBlock) newReviewBlock.replaceWith(preservedReviewBlock);
    }

    /* Restore the preserved <king-linked-options-product> swatch
       container with its existing slide transform intact. Then move
       the `.lv-option-item__active` class to the swatch matching the
       new product's handle so the highlight border tracks the click. */
    if (preservedSwatchEl) {
      var newSwatchEl = section.querySelector('king-linked-options-product');
      if (newSwatchEl) newSwatchEl.replaceWith(preservedSwatchEl);
      /* Find the swatch link whose data-handle matches the swatch the
         user clicked, and toggle the active state. We pull the handle
         from the click target instead of guessing — robust to renames.
         Active state lives on TWO levels of the swatch DOM:
           - `a.lv-option-item` gets the `lv-option-item__active` class
             (its border).
           - The inner `div.lv-option-swatch.lv-option-image-swatch` gets
             two extra classes: `lv-option-swatch__active` and
             `lv-option-image-swatch__active` (the actual visible
             selection styling — orange border, scale, etc.).
         We have to move all three classes together or the visual
         border ends up on the wrong swatch. */
      var clickedHandle = (function () {
        try { return new URL(fallbackUrl).pathname.split('/').pop(); } catch (e) { return null; }
      })();
      if (clickedHandle) {
        var oldActive = preservedSwatchEl.querySelector('a.lv-option-item__active');
        if (oldActive) {
          oldActive.classList.remove('lv-option-item__active');
          var oldInner = oldActive.querySelector('.lv-option-swatch');
          if (oldInner) {
            oldInner.classList.remove('lv-option-swatch__active');
            oldInner.classList.remove('lv-option-image-swatch__active');
          }
        }
        var newActive = preservedSwatchEl.querySelector(
          'a.lv-option-item-link[data-handle="' + clickedHandle + '"]'
        );
        if (newActive) {
          newActive.classList.add('lv-option-item__active');
          var newInner = newActive.querySelector('.lv-option-swatch');
          if (newInner) {
            newInner.classList.add('lv-option-swatch__active');
            newInner.classList.add('lv-option-image-swatch__active');
          }
        }
      }

      /* Force a smooth animation when Linked Options' JS later updates
         the slide transform for the new active. The .lv-option-slide-
         items already has `transition: transform 0.5s` in its CSS, but
         LO bypasses transitions when it sets a new transform (likely
         via `transition: none` + reflow + new value). We watch for
         that next mutation, then re-apply the "old → new" change with
         the transition enabled so the user sees a smooth slide instead
         of a 200px snap. */
      var slideItems = preservedSwatchEl.querySelector('.lv-option-slide-items');
      if (slideItems) {
        var oldTransform = slideItems.style.transform || '';
        var observer = new MutationObserver(function () {
          var newTransform = slideItems.style.transform || '';
          if (!newTransform || newTransform === oldTransform) return;
          observer.disconnect();
          /* Reset to old, force reflow, then animate to LO's new value. */
          slideItems.style.transition = 'none';
          slideItems.style.transform = oldTransform;
          /* Reading offsetHeight forces synchronous reflow so the next
             transform change is treated as a transition trigger. */
          slideItems.getBoundingClientRect();
          slideItems.style.transition = 'transform 0.5s ease';
          slideItems.style.transform = newTransform;
        });
        observer.observe(slideItems, { attributes: true, attributeFilter: ['style'] });
        /* Safety auto-disconnect — if LO never updates the transform
           we don't want this observer hanging around forever. */
        setTimeout(function () { observer.disconnect(); }, 5000);
      }
    }

    /* Insert the shroud at the top of the new buy_buttons block-item.
       Kaching's `<kaching-bundle>` element is NOT in the section's
       server-rendered HTML — it's injected dynamically by Kaching's
       MutationObserver in the app-embed loader after the new buy_buttons
       appears. The shroud is a plain <div> with the renamed-tag snapshot
       of the OLD bundle, so it doesn't satisfy Kaching's MO "is there
       a bundle here?" check — Kaching still goes ahead and injects a
       fresh bundle for the new product.
       The `[data-lo-has-preserved]` section flag activates the CSS
       rule that hides the new (real) kaching-bundle while the shroud
       is up. */
    if (preservedShroud) {
      /* `seamlessKaching()` check already gated `preservedShroud` creation
         above, so reaching here means we have a shroud to install. */
      var newBb = section.querySelector('[data-block-id="buy_buttons"]');
      if (newBb) {
        preservedShroud.style.display = 'block';
        if (reservedKachingBlockHeight) {
          preservedShroud.style.minHeight = reservedKachingBlockHeight + 'px';
        }
        newBb.insertBefore(preservedShroud, newBb.firstChild);
        section.dataset.loHasPreserved = '1';
      }
    }

    /* Title — keep the on-load suffix (" – Shop Name" or similar). */
    if (parsed.title) {
      document.title = parsed.title + titleSuffix;
    }

    /* Courtesy event for anything else listening for section reloads. */
    document.dispatchEvent(new CustomEvent('shopify:section:load', {
      bubbles: true,
      detail: { sectionId: SECTION_ID }
    }));
  }

  /* Kaching Bundles renders via an app embed at the document level (NOT
     inside the section). Its product config lives in
     <script class="kaching-bundles-product"> tags, which are NOT in the
     section response. So our innerHTML swap leaves Kaching's injected
     <kaching-bundles-block> elements wiped, and its cached product config
     stale.
     Fix: extract the new product's kaching-bundles-product and
     kaching-bundles-deal-block-settings scripts from a full-page fetch,
     swap them in, then call `kachingBundlesInitialize()` to re-render
     bundles in the new section.
     The init function has a side-effect we have to undo: it writes its
     own "selected variant" back into the form's input[name="id"]. We
     capture the correct variant id before calling init and restore it
     after. */
  function applyKachingUpdate(pageHtml, myClickId) {
    /* myClickId — the click ID at the moment this swap started. Used by
       the finishLoading polling loop and the vid-defender teardown to
       detect if a NEWER click has superseded this swap, in which case
       we bail out and let the newer click's applyKachingUpdate handle
       cleanup. Without this guard, the older swap's polling/teardown
       races with the newer swap and nukes its in-flight state (e.g.
       removes the newer click's shroud or disconnects its vid defender).
       Default to latestClickId when unspecified (call sites should
       always pass it, but be safe). */
    if (typeof myClickId !== 'number') myClickId = latestClickId;
    /* Helper for early-return paths to unlock bundle interactions
       (set on the section by handleSwatchClick). Without this, any
       early-return path leaves the bundle perpetually click-locked
       for the rest of the session. */
    var earlySection = document.getElementById(SECTION_DOM_ID);
    var releaseBundleLock = function () {
      /* Tear down everything applySwap set up: drop the section flags
         AND remove the preserved-OLD bundle (otherwise the user gets
         stuck looking at the OLD bundle forever with no path to the
         new one). On the wholesale / Kaching-missing paths there's
         nothing to fall back to, so we accept the bundle staying
         empty until a real page load. */
      if (!earlySection) return;
      var shroud = earlySection.querySelector('[data-lo-bundle-shroud]');
      if (shroud) shroud.remove();
      delete earlySection.dataset.loBundleLoading;
      delete earlySection.dataset.loHasPreserved;
    };
    /* `pageHtml === false` means the fetch genuinely failed (e.g.
       Cloudflare 429 rate-limit). The section was already swapped by
       applySwap but Kaching's scripts are stale — if we just release
       the lock, the user sees an empty bundle area with no path to
       recover except clicking another swatch (which might also 429).
       Reload the page so the user gets a clean, fully-rendered state.
       `pageHtml === null` means the fetch was deliberately skipped
       (wholesale page, kachingOnPage=false). No reload — the page
       genuinely doesn't have Kaching to update. */
    if (pageHtml === false) {
      console.warn('[LO seamless] page fetch failed — full-reload to recover bundle');
      window.location.reload();
      return;
    }
    if (!pageHtml || typeof window.kachingBundlesInitialize !== 'function') {
      releaseBundleLock();
      return;
    }
    var parser = new DOMParser();
    var doc = parser.parseFromString(pageHtml, 'text/html');
    var newScripts = doc.querySelectorAll(
      'script.kaching-bundles-product, script.kaching-bundles-deal-block-settings'
    );
    if (!newScripts.length) {
      releaseBundleLock();
      return;
    }

    /* Remove the page's existing kaching product/deal scripts; we keep
       the `kaching-bundles-config` (shop-level, not product-specific). */
    var existing = document.querySelectorAll(
      'script.kaching-bundles-product, script.kaching-bundles-deal-block-settings'
    );
    var anchor = existing.length ? existing[existing.length - 1].nextSibling : null;
    var parent = existing.length ? existing[existing.length - 1].parentNode : document.body;
    existing.forEach(function (s) { s.parentNode.removeChild(s); });

    /* Cloning a <script> node from another document doesn't execute its
       contents — but Kaching reads these as data (textContent), not as
       executable code, so a static clone is fine. */
    newScripts.forEach(function (s) {
      var clone = document.createElement('script');
      Array.from(s.attributes).forEach(function (a) { clone.setAttribute(a.name, a.value); });
      clone.textContent = s.textContent;
      parent.insertBefore(clone, anchor);
    });

    /* Capture the server-rendered (correct) variant id from the new
       section, so we can restore it if init mutates it. */
    var section = document.getElementById(SECTION_DOM_ID);
    var correctVid = null;
    if (section) {
      var form = section.querySelector('form[action*="/cart/add"]');
      var input = form && form.querySelector('input[name="id"]');
      if (input) correctVid = input.value;
    }

    /* Init Kaching with the new scripts. This synchronously rebuilds
       the <kaching-bundles-block> inside the empty <kaching-bundle>
       placeholder. The three deal bars render synchronously; the
       upsell bar lands async ~150-300ms later.
       Defensive: ensure an empty <kaching-bundle> exists in
       buy_buttons before calling init. The section response from the
       Section Rendering API doesn't include the kaching-bundle (it's
       app-embed-injected, not in the section template). On a normal
       page load Kaching's loader creates it on first paint. On our
       swap, the previous kaching-bundle was wiped by innerHTML, and
       Kaching's init has been observed to silently no-op if there's
       no existing element to fill — likely because it caches a
       reference to the previously-rendered element and assumes
       "already initialized". By pre-creating an empty element, we
       give init a fresh target and force it to re-render. Repro:
       fast-switch from A to B mid-A-render → A's init created+wiped
       cycle leaves Kaching's state thinking it's done → B's init
       doesn't re-render → bundle invisible until full reload. */
    var section = document.getElementById(SECTION_DOM_ID);
    var buyButtons = section && section.querySelector('[data-block-id="buy_buttons"]');
    if (buyButtons && !section.querySelector('kaching-bundle')) {
      var emptyKb = document.createElement('kaching-bundle');
      buyButtons.insertBefore(emptyKb, buyButtons.firstChild);
    }
    try { window.kachingBundlesInitialize(); } catch (e) { /* no-op */ }

    /* Restore the variant id. kachingBundlesInitialize's bundle state
       update synchronously mutates the form's input[name="id"] back
       to whatever its first deal bar references — we capture the
       correct vid BEFORE init and restore here. */
    if (correctVid && section) {
      var input2 = section.querySelector('form[action*="/cart/add"] input[name="id"]');
      if (input2 && input2.value !== correctVid) input2.value = correctVid;
    }

    /* Tear down the vid defender installed by applySwap. Kaching's
       internal state is now NEW (post-init), so its `_updateIdInput`
       calls from this point write the correct value on their own —
       the defender's job is done. Leaving it connected would mean a
       perpetual MutationObserver firing on every variant-id change
       for the rest of the session.
       Identity guard: only disconnect if the defender's target matches
       OUR correctVid. A newer click might have already replaced the
       defender with one targeting a different vid — disconnecting THAT
       would leave the newer swap with no vid protection. */
    if (section && section.__loVidDefender &&
        section.__loVidDefenderTarget === correctVid) {
      try { section.__loVidDefender.disconnect(); } catch (e) { /* no-op */ }
      section.__loVidDefender = null;
      section.__loVidDefenderTarget = null;
    }

    /* Wait for the new bundle to be visually present (deal bars in DOM),
       apply the user's pre-swap selection if any, release the bundle's
       min-height pin so it can size naturally, then drop the loading
       flag — at which point the CSS un-dims the bundle and the spinner
       disappears.
       The state-apply happens BEFORE clearing the flag, so it runs
       while the bundle is still dimmed and the user never sees the
       intermediate default state. */
    var stateToRestore = pendingKachingState;
    pendingKachingState = null;
    /* Target the bar count we measured pre-swap — this avoids the
       "3 deal bars un-dim → 4th upsell pops in 100ms later" glitch.
       Fallback to 3 if metrics never got captured (e.g. first-ever
       click happened before Kaching finished its initial render). */
    var targetBarCount = reservedKachingBarCount || 3;
    var deadline = Date.now() + 3000;
    var finishLoading = function () {
      /* Stale-click guard. If a newer click has superseded us, abandon
         this polling loop immediately — the newer click's own
         applyKachingUpdate / finishLoading is responsible for the
         eventual cleanup. Without this, an older finishLoading that
         keeps polling can race against the newer swap: timing out and
         running cleanup (`removes shroud`, `clears flags`,
         `applyKachingStateSync with stale state`) just as the newer
         swap is mid-install. Net result: newer swap's shroud yanked
         out from under it, state restored to the WRONG selection, etc.
         Also re-stash pendingKachingState so the newer click's
         applyKachingUpdate can still consume it (we already nulled it
         when entering applyKachingUpdate — but if we're now stale,
         we should give it back). */
      if (myClickId !== latestClickId) {
        if (stateToRestore && !pendingKachingState) {
          pendingKachingState = stateToRestore;
        }
        return;
      }
      /* Look at the NEW kaching-bundle (Kaching renders into the one
         without the preserved marker) — not the preserved OLD bundle
         that's currently visible to the user. */
      var kb = section ? section.querySelector('kaching-bundle') : null;
      var bars = kb ? kb.querySelectorAll('.kaching-bundles__bar') : [];
      var ready = bars.length >= targetBarCount;
      if (!ready && Date.now() < deadline) {
        setTimeout(finishLoading, 50);
        return;
      }
      /* Either ready or timed out — try state restoration if we have
         bars to restore into, then ALWAYS run cleanup (try/finally)
         so a thrown state-restore can never leave the loader stuck.
         Worst case: state restore fails, user lands in default
         Kaching state but the page is interactive. */
      try {
        if (stateToRestore && bars.length) {
          applyKachingStateSync(section, stateToRestore);
        }
      } catch (e) {
        console.warn('[LO seamless] state restore threw, skipping', e);
      } finally {
        if (kb) kb.style.minHeight = '';
        var shroud = section ? section.querySelector('[data-lo-bundle-shroud]') : null;
        if (shroud) shroud.remove();
        if (section) {
          delete section.dataset.loBundleLoading;
          delete section.dataset.loHasPreserved;
        }
      }
    };
    finishLoading();
  }

  function handleSwatchClick(e) {
    /* Modifier keys, middle-click, right-click → fall through to default nav. */
    if (e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    var link = e.target.closest('a.lv-option-item-link');
    if (!link) return;
    if (!link.dataset || !link.dataset.handle) return;

    /* Capture-phase + stopImmediatePropagation — Linked Options ships its
       own click handler that programmatically navigates, ignoring
       preventDefault. We must beat their handler to the click and stop the
       event chain before it fires. */
    e.preventDefault();
    e.stopImmediatePropagation();

    if (link.classList.contains('lv-option-item__active')) {
      /* Already-selected swatch — nothing to do, but we still stopped the
         click so we don't reload the same product. */
      return;
    }

    var handle = link.dataset.handle;
    var href = link.href;

    /* Cancel any in-flight fetch from a previous swatch click. */
    if (activeController) {
      try { activeController.abort(); } catch (e) { /* no-op */ }
    }
    activeController = new AbortController();
    var signal = activeController.signal;
    var myClickId = ++latestClickId;

    /* Disable the Add-to-Cart form while the swap is in flight (see the
       `injectLoaderStyles` comment). The data-attribute is set on the
       section element which survives `section.innerHTML = ...`, so the
       disabled-state applies to BOTH the old form (between click and
       section swap) AND the new form (between section swap and
       applyKachingUpdate completion). */
    var section = document.getElementById(SECTION_DOM_ID);
    if (section) {
      section.dataset.loSwapInProgress = '1';
      /* Bundle-area lock — independent of the form lock above. Clears
         later (after Kaching re-renders the bundle and state is
         re-applied) so the user's mid-swap clicks on the bundle don't
         get silently undone by the upcoming re-render. Cleared in
         applyKachingUpdate's tail.
         Only set when there's actually a Kaching bundle on the page.
         Two cases get a `false` here:
           - Wholesale customers (Kaching block isn't rendered server-side).
           - Retail products that simply don't have Kaching deals
             configured (the <kaching-bundle> element exists but Kaching
             never filled it with bars — kachingOnPage() detects this).
         Setting the flag in either case would hide the quantity
         stepper for no reason. The ATC button still gets its own
         spinner via data-lo-swap-in-progress above, which is the
         right loading affordance when there's no bundle. */
      if (kachingOnPage()) {
        section.dataset.loBundleLoading = '1';
      }
    }
    var clearLoadingFlag = function () {
      /* Only clear if THIS click is still the latest one. A newer click
         that started after us is responsible for its own clear. */
      if (myClickId === latestClickId && section) {
        delete section.dataset.loSwapInProgress;
      }
    };
    /* Force-cleanup helper for error / timeout paths. Drops the shroud
       and bundle-loading flags so the user can never get stuck looking
       at a loading state forever. Safe to call multiple times — each
       op is a no-op if the relevant element/attribute is already gone. */
    var forceClearBundleLoading = function () {
      if (!section) return;
      var shroud = section.querySelector('[data-lo-bundle-shroud]');
      if (shroud) shroud.remove();
      delete section.dataset.loBundleLoading;
      delete section.dataset.loHasPreserved;
    };
    /* Watchdog: if anything downstream throws / hangs and finishLoading
       never runs to completion, this fires after a hard ceiling and
       drops the loading state so the user is unblocked. Only fires if
       this click is still the latest one — a newer click is responsible
       for its own watchdog. Ceiling is generous (longer than the
       finishLoading's own 3s deadline + room for page-fetch slowness)
       so it doesn't pre-empt the normal completion path. */
    setTimeout(function () {
      if (myClickId === latestClickId && section &&
          section.dataset.loBundleLoading === '1') {
        console.warn('[LO seamless] bundle-loading watchdog fired — force-clearing stuck state');
        forceClearBundleLoading();
      }
    }, 10000);

    /* Capture the user's current Kaching selection (which deal bar +
       which upsells are checked) so we can re-apply it after the new
       product's bundle re-renders. Stored at module scope so
       applyKachingUpdate (later in the promise chain) can consume it.
       Null means "default state, nothing to restore".
       Don't overwrite an already-pending state. Rapid-click scenario:
       user is on rage-within with bar 1 + upsell, clicks sundered
       (Click A), then immediately clicks shattered (Click B) before
       A's swap completes. At Click B's time, the kaching-bundle in
       the DOM might be A's hidden new injection in default state
       (because A's applyKachingUpdate hasn't restored yet) — capturing
       from THAT would return null/default and the user would lose
       their original rage-within selection. Click A already captured
       the correct pre-swap state (bar 1 + upsell), so Click B should
       leave it alone and let it flow through to B's applyKachingUpdate.
       Cleared (set to null) by applyKachingUpdate when it consumes
       the state — so the NEXT click after a completed swap captures
       fresh from the actual visible bundle. */
    if (!pendingKachingState) {
      pendingKachingState = captureKachingState(section);
    }

    var promises = fetchProductBundle(handle, signal);

    /* Apply the section swap as soon as the (fast) section response arrives.
       An UNEXPECTED exception inside applySwap (e.g. an upstream app shipped
       a breaking change that nukes a selector we rely on) should not leave
       the user stuck on a half-swapped page — fall back to full nav. */
    var sectionDone = promises.sectionPromise
      .then(function (bundle) {
        /* Stale click — newer one in flight, drop this result. */
        if (myClickId !== latestClickId) return null;
        try {
          applySwap(bundle, href);
          /* Re-enable ATC after the section is in place — DON'T wait for
             the page fetch (which feeds Kaching's re-init); on slow or
             saturated networks that can be many seconds.
             Deferred to a macrotask (setTimeout 0) so every microtask
             that runs after `section.innerHTML = ...` has settled
             before the flag clears. The relevant microtasks are:
               1. Kaching's MutationObserver callback fires
                  `_updateIdInput` and writes the OLD product's vid into
                  the form's input[name="id"]. (Kaching's bundle state
                  still holds the previous product's data — it's only
                  refreshed later in applyKachingUpdate.)
               2. Our applySwap-installed vid defender MO fires next
                  and re-writes the correct NEW vid back.
             If we cleared the flag synchronously (inside applySwap's
             same sync tick), the user could click ATC in the ~1ms
             window between Kaching's overwrite and the defender's
             correction. Macrotask-defer pushes the clear past both
             microtasks, so by the time ATC is clickable, the form
             holds the correct vid. */
          setTimeout(clearLoadingFlag, 0);
          return true;
        } catch (e) {
          console.warn('[LO seamless] applySwap threw, falling back to full nav', e);
          window.location.href = href;
          return null;
        }
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return null;
        if (myClickId !== latestClickId) return null;
        console.warn('[LO seamless] section fetch failed, falling back to full nav', err);
        window.location.href = href;
        return null;
      });

    /* Refresh Kaching ONLY after the section swap has been applied. The
       page fetch can resolve BEFORE the section fetch (e.g., when cached
       from a previous visit), but Kaching update must operate on the
       post-swap DOM — otherwise the cross-fade overlay would be cloned
       from the un-patched OLD bundle and end up showing the wrong
       content. Gate on `sectionDone` to enforce ordering.
       Failures here are SWALLOWED rather than triggering a full nav:
       the section swap already succeeded, the user is on the right page,
       and a stale bundle is a much smaller regression than yanking them
       to a full reload after the visible swap completed. */
    Promise.all([sectionDone, promises.pagePromise])
      .then(function (results) {
        var sectionApplied = results[0];
        var pageHtml = results[1];
        if (!sectionApplied) return;
        if (myClickId !== latestClickId) return;
        try {
          applyKachingUpdate(pageHtml, myClickId);
        } catch (e) {
          console.warn('[LO seamless] applyKachingUpdate threw, bundle may show stale state', e);
          /* applyKachingUpdate is what would normally clear the loading
             flags via finishLoading — if it threw before reaching that
             point, the flags are stuck. Force-clear so the user isn't. */
          if (myClickId === latestClickId) forceClearBundleLoading();
        }
      })
      .catch(function () {
        /* Promise.all rejected unexpectedly (both child promises swallow
           their own errors, so this is rare — defense in depth). */
        if (myClickId === latestClickId) forceClearBundleLoading();
      });
  }

  /* No popstate handler. We use replaceState (not pushState) for variant
     switches, so variant URLs don't add to the back stack. Back-button
     goes to whatever page the user came from. */

  /* Capture phase on `window` so we fire EARLIER in the event flow than
     Linked Options' own document-level handler. Combined with
     stopImmediatePropagation in the handler, this prevents their JS
     navigation from running for matched swatches.
     Event capture order: window → document → html → body → … → target. */
  window.addEventListener('click', handleSwatchClick, { capture: true });
})();
